import { fraudBlockMessage, isBlockedByFraud } from '@/helpers/fraudReview'
import { recordHasRemit, latestRemitDiffersFromRecord, remitFieldDiffers } from '@/helpers/remit'
import { deliveryEntryDiffersFromReference, deliveryFieldDiffers, getLatestDelivery, latestDeliveryDiffersFromRecord } from '@/helpers/deliveryMass'

const NOVELTY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

function intervalSecures (value) {
  if (value <= 150000) return 50000
  if (value <= 400000) return 100000
  return 200000
}

function getPackageName (record) {
  if (record.brand === 'bassica') return 'xs'
  if (record.brand === 'sahet') {
    const value = record.value || record.total || 0
    if (value > 0 && value <= 250000) return 's'
    if (value > 250000 && value <= 400000) return 'm'
    if (value > 400000 && value <= 700000) return 'l'
    if (value > 700000) return 'xl'
  }
  return 's'
}

export function enrichDataNoveltyOrder (order, preserve = {}) {
  const record = { ...order }

  record.value = record.total || 0
  record.declared = preserve.declared !== undefined
    ? preserve.declared
    : intervalSecures(record.total || 0)
  record.package = preserve.package !== undefined ? preserve.package : getPackageName(record)
  record.coordinadoraCost = preserve.coordinadoraCost
  record.packageDimensions = preserve.packageDimensions

  return record
}

export function remitIsUpToDate (order) {
  return recordHasRemit(order) && !latestRemitDiffersFromRecord(order)
}

export function latestDeliveryHasActiveGuide (order) {
  const latest = getLatestDelivery(order)
  if (!latest || latest.status === 'declined') return false
  return !!(latest.idRemission || latest.tracking || latest.idWelivery)
}

export function canQuoteDataNovelty (order) {
  if (!remitIsUpToDate(order)) return false

  const latest = getLatestDelivery(order)
  if (!latest || latest.status === 'declined') return true
  if (latest.idRemission && !deliveryEntryDiffersFromReference(latest, order)) return false
  return true
}

export function canGenerateDataNoveltyGuide (order) {
  if (!order || !order.coordinadoraCost) return false
  if (order.userPayConveyor) return false
  if (latestDeliveryDiffersFromRecord(order)) return false

  const latest = getLatestDelivery(order)
  if (latest && latest.idRemission && !deliveryEntryDiffersFromReference(latest, order)) {
    return false
  }

  return true
}

export function canGenerateDataNoveltyCollectGuide (order) {
  if (!order || !order.coordinadoraCost || !order.userPayConveyor) return false
  if (latestDeliveryDiffersFromRecord(order)) return false

  const latest = getLatestDelivery(order)
  if (latest && latest.idRemission && !deliveryEntryDiffersFromReference(latest, order)) {
    return false
  }

  return true
}

export function canCloseDataNovelty (order) {
  return remitIsUpToDate(order)
}

export function displayDataNoveltyFreightCost (order) {
  const delivery = getLatestDelivery(order)
  if (delivery && delivery.status === 'declined') return order.coordinadoraCost || null
  if (delivery && delivery.estimatedCost && !delivery.cost) return delivery.estimatedCost
  if (delivery && delivery.cost) return delivery.cost
  return order.coordinadoraCost || null
}

export function needsNewDeliveryForDataNovelty (order) {
  const latest = getLatestDelivery(order)
  if (!latest) return true
  if (latest.status === 'declined') return true
  if (latest.idRemission && deliveryEntryDiffersFromReference(latest, order)) return true
  return false
}

export function getOrderNoveltySinceDate () {
  return new Date(Date.now() - NOVELTY_WINDOW_MS)
}

export function orderHasRecentNoveltyData (order) {
  if (!order || !order.hasNoveltyData || !order.noveltyData || !order.noveltyData.length) {
    return false
  }

  const since = getOrderNoveltySinceDate().getTime()

  return order.noveltyData.some(entry => {
    if (!entry || !entry.createdAt) return true
    return new Date(entry.createdAt).getTime() >= since
  })
}

export function orderHasRecentProductChange (order) {
  if (!order || !order.hasProductChange || !order.productChange) {
    return false
  }

  const since = getOrderNoveltySinceDate().getTime()
  const createdAt = order.productChange.createdAt

  if (!createdAt) return true
  return new Date(createdAt).getTime() >= since
}

const REMIT_PATCH_KEYS = [
  'remitData',
  'remitDataGenerated',
  'dataLocked',
  'name',
  'location',
  'department',
  'address',
  'phones'
]

export function buildRemitPatch (source) {
  if (!source) return {}

  const patch = {}

  REMIT_PATCH_KEYS.forEach(key => {
    if (source[key] === undefined) return
    patch[key] = source[key]
  })

  return patch
}

export function mergeNoveltyOrder (base, next) {
  const merged = Object.assign({}, base || {}, next || {})

  if (!next || !next.phones || !next.phones.length) {
    merged.phones = (base && base.phones) || []
  }

  if (!next || next.address === undefined) {
    merged.address = base && base.address
  }

  if (!next || next.remitData === undefined) {
    merged.remitData = (base && base.remitData) || []
    merged.remitDataGenerated = !!(base && base.remitDataGenerated)
  }

  if (!next || next.dataLocked === undefined) {
    merged.dataLocked = !!(base && base.dataLocked)
  }

  return merged
}

export function mapOrderDataNoveltyListItem (order) {
  return enrichDataNoveltyOrder({
    order: order.order,
    status: order.status,
    name: order.name,
    location: order.location,
    department: order.department,
    address: order.address,
    phones: order.phones || [],
    brand: order.brand,
    createdAt: order.createdAt,
    hasNoveltyData: !!order.hasNoveltyData,
    noveltyData: order.noveltyData || [],
    remitData: order.remitData || [],
    remitDataGenerated: !!order.remitDataGenerated,
    dataLocked: !!order.dataLocked,
    total: order.total || 0,
    userPayConveyor: !!order.userPayConveyor,
    deliveries: order.deliveries || []
  }, {
    coordinadoraCost: order.coordinadoraCost,
    declared: order.declared,
    package: order.package,
    packageDimensions: order.packageDimensions
  })
}

export function mapOrderProductChangeListItem (order) {
  return {
    order: order.order,
    status: order.status,
    name: order.name,
    location: order.location,
    department: order.department,
    address: order.address,
    phones: order.phones || [],
    brand: order.brand,
    createdAt: order.createdAt,
    hasProductChange: !!order.hasProductChange,
    productChange: order.productChange || null,
    productChangeHistory: order.productChangeHistory || [],
    remitData: order.remitData || [],
    remitDataGenerated: !!order.remitDataGenerated,
    dataLocked: !!order.dataLocked
  }
}

export function buildProductChangeDetailOrder (order) {
  if (!order || !order.productChange || !order.productChange.item) return null

  return {
    ...order,
    cart: [order.productChange.item]
  }
}

export const PRODUCT_CHANGE_REJECT_REASONS = [
  { key: 'already_sent', label: 'El pedido ya se había enviado' },
  { key: 'not_found', label: 'Producto no se encontró en la bodega' }
]

export function isBlockedByProductChange (order) {
  return !!(order && order.hasProductChange)
}

export function productChangeBlockMessage (order) {
  if (!isBlockedByProductChange(order)) return ''

  const change = order.productChange || {}
  const product = change.product || ''
  const from = change.from || ''
  const to = change.to || ''

  if (product && from && to) {
    return `Cambio de talla pendiente (${product}: ${from} → ${to}). Resuélvelo en Novedades antes de empacar.`
  }

  return 'Cambio de producto pendiente. Resuélvelo en Novedades antes de empacar.'
}

export function orderPackBlockMessage (order) {
  if (!order) return ''
  if (isBlockedByFraud(order)) return fraudBlockMessage(order)
  return productChangeBlockMessage(order)
}

const ADDRESS_NOVELTY_CHANGES = ['changed address', 'changed city', 'changed department', 'changed location']

export function isBlockedByAddressNovelty (order) {
  if (!order || !order.hasNoveltyData) return false

  const remits = order.remitData || []
  const lastRemit = remits[remits.length - 1]
  if (lastRemit && (remitFieldDiffers(lastRemit, order, 'address') || remitFieldDiffers(lastRemit, order, 'location'))) {
    return true
  }

  const lastDelivery = getLatestDelivery(order)
  if (lastDelivery && lastDelivery.status !== 'declined') {
    if (deliveryFieldDiffers(lastDelivery, order, 'address') || deliveryFieldDiffers(lastDelivery, order, 'location')) {
      return true
    }
  }

  if (!lastRemit) {
    return (order.noveltyData || []).some(entry => (
      entry && ADDRESS_NOVELTY_CHANGES.includes(entry.change)
    ))
  }

  return false
}

export function addressNoveltyBlockMessage (order) {
  if (!isBlockedByAddressNovelty(order)) return ''
  return 'Novedad de cambio de dirección pendiente. Resuélvela en Novedades antes de enviar.'
}

export function orderSendBlockMessage (order) {
  if (!order) return ''
  if (isBlockedByFraud(order)) return fraudBlockMessage(order)
  return addressNoveltyBlockMessage(order)
}



// WEBPACK FOOTER //
// ./src/helpers/orderNovelty.js